import { parseIsoDateAsUtcDay } from "encamp-shared/src/utils/date";
import { dateToDateString } from "encamp-shared/src/utils/dateToString";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import {
  contactTimeliness as sharedContactTimeliness,
  epaContactDeadline,
  plannedFilingTimeliness as sharedPlannedFilingTimeliness,
  type ContactTimeliness,
  type FilingTimeliness,
} from "encamp-waste-accumulation";
import {
  EpisodicEpaFilingFieldsFragment,
  EpisodicReliefUnavailableReason,
  SubmissionStatus,
  UnitType,
  WasteContainerStatus,
  WasteEpisodicEventContactMethod,
  WasteEpisodicEventNoticeKind,
  WasteEpisodicEventStatus,
  WasteEpisodicEventType,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { getDateLikeIsoDate } from "util/dates";

import { formatContainerDate } from "../Containers/containerDisplay";

export const NO_VALUE_PLACEHOLDER = "—";

export const EVENT_TYPE_LABELS: Record<WasteEpisodicEventType, string> = {
  [WasteEpisodicEventType.Planned]: "Planned",
  [WasteEpisodicEventType.Unplanned]: "Unplanned",
};

/** The status chip. "Expired" is derived: an ACTIVE event past its deadline. */
export function eventStatusChip(
  event: { status: WasteEpisodicEventStatus; accumulationDeadline: string },
  now: DateTime = DateTime.now()
): { label: string; color: "warning" | "success" | "error" } {
  if (event.status === WasteEpisodicEventStatus.Finalized) {
    return { label: "Finalized", color: "success" };
  }
  if (isEventWindowLapsed(event.accumulationDeadline, now)) {
    return { label: "Expired", color: "error" };
  }
  return { label: "Active", color: "warning" };
}

/**
 * A compact display range for an event's start/end `Date` scalars, e.g.
 * "Jun 28 – Jul 3, 2026". The year is dropped from the start side when both
 * dates fall in the same calendar year, matching the container date-handling
 * idiom of parsing in UTC (see `formatContainerDate`) since these are
 * date-only values with no time-of-day to shift.
 */
export function formatEventWindow(startDate: string, endDate: string): string {
  const start = DateTime.fromISO(startDate, { zone: "utc" });
  const end = DateTime.fromISO(endDate, { zone: "utc" });
  if (!start.isValid || !end.isValid) {
    return NO_VALUE_PLACEHOLDER;
  }
  const startFormat = start.year === end.year ? "MMM d" : "MMM d, yyyy";
  return `${start.toFormat(startFormat)} – ${end.toFormat("MMM d, yyyy")}`;
}

/**
 * Minimal shape `resolveFormObligation` needs from a submission row.
 * `submittedAt` is optional so existing status-only literals (fixtures,
 * other callers) keep compiling; missing/undefined is treated the same as
 * `null` — a filing timeliness verdict can't be produced without it.
 */
export type NotificationCellSubmission = {
  status: SubmissionStatus;
  submittedAt?: string | null;
};

/**
 * The event's completed EPA filing, if any. Used by `resolveFormObligation`
 * (and directly by `ObligationsSection`'s `FiledFormAlert`) so both apply the
 * same "a completed filing wins" rule.
 */
export function filedSubmission<S extends NotificationCellSubmission>(event: {
  submissions: S[];
}): S | undefined {
  return event.submissions.find(
    (submission) => submission.status === SubmissionStatus.FilingComplete
  );
}

/**
 * The event's in-progress submission, if nothing has been filed yet. Used by
 * `resolveFormObligation` (and directly by `ObligationsSection`'s
 * `SubmissionInProgressAlert`).
 */
export function openSubmission<S extends NotificationCellSubmission>(event: {
  submissions: S[];
}): S | undefined {
  return event.submissions.find(
    (submission) => submission.status !== SubmissionStatus.FilingComplete
  );
}

/** Minimal shape of a `WasteEpisodicEventNotice` needed for obligation-state derivation. */
export type EpisodicNotice = {
  kind: WasteEpisodicEventNoticeKind;
  noticeDate: string;
  method?: WasteEpisodicEventContactMethod | null;
  document?: { id: string; title: string; fileExtension?: string | null } | null;
  epaFiling?: EpisodicEpaFilingFieldsFragment | null;
};

/** Display label for an EPA_CONTACT notice's contact method. */
export const CONTACT_METHOD_LABELS: Record<WasteEpisodicEventContactMethod, string> = {
  [WasteEpisodicEventContactMethod.Phone]: "Phone",
  [WasteEpisodicEventContactMethod.Email]: "Email",
  [WasteEpisodicEventContactMethod.Fax]: "Fax",
};

/**
 * Whether the card should prompt the "did you contact the EPA?" question:
 * only for an unplanned event, with a known start date, whose 72-hour contact
 * window has fully elapsed. Judged as plain calendar days — the SME reading
 * is that time zones never matter here — so the question stays optional
 * through the whole of day 3 (`contactDueDate`) and only becomes required
 * once today's UTC calendar date is strictly after it, i.e. day 4. `now` is
 * injectable for tests.
 */
export function requiresEpaContactQuestion(
  type: WasteEpisodicEventType,
  startDate: string | null,
  now: DateTime = DateTime.now()
): boolean {
  if (type !== WasteEpisodicEventType.Unplanned || startDate == null) {
    return false;
  }
  const today = now.toUTC().startOf("day");
  const dueDate = DateTime.fromISO(contactDueDate(startDate), { zone: "utc" });
  return today > dueDate;
}

export type { ContactTimeliness, FilingTimeliness };

/**
 * Judges a recorded EPA-contact date against an event's start date, both
 * date-only scalars compared in UTC (see `formatEventWindow`). The 72-hour
 * rule is judged leniently at this date-only granularity — a contact on the
 * same calendar day as `startDate + 3 days` counts as timely, since the
 * underlying rule is denominated in hours, not calendar days, and only an
 * unambiguous overrun should read as a violation.
 */
export function contactTimeliness(startDate: string, contactDate: string): ContactTimeliness {
  return sharedContactTimeliness(
    parseIsoDateAsUtcDay(startDate),
    parseIsoDateAsUtcDay(contactDate)
  );
}

/**
 * Judges a planned event's Form 8700-12 filing date against its 30-day
 * advance-notice rule, both date-only scalars compared in UTC. Only
 * meaningful for PLANNED events — unplanned 8700-12 filings have no
 * deadline, so callers gate on `type` before reaching for this (see
 * `filingTimelinessForPlannedEvent`).
 */
export function plannedFilingTimeliness(startDate: string, filedDate: string): FilingTimeliness {
  return sharedPlannedFilingTimeliness(
    parseIsoDateAsUtcDay(startDate),
    parseIsoDateAsUtcDay(filedDate)
  );
}

/**
 * `plannedFilingTimeliness`, gated on the event actually being PLANNED and
 * on a filing date existing at all — an unplanned 8700-12 has no deadline
 * (never gets a verdict), and a filing that hasn't happened yet obviously
 * can't be judged. Shared by `resolveFormObligation`'s `filed` and
 * `filedExternally` branches so both apply the same gate.
 */
function filingTimelinessForPlannedEvent(
  type: WasteEpisodicEventType,
  startDate: string,
  filedDate: string | null | undefined
): FilingTimeliness | null {
  if (type !== WasteEpisodicEventType.Planned || filedDate == null) {
    return null;
  }
  return plannedFilingTimeliness(startDate, filedDate);
}

/**
 * Whether a notice date (date-only, UTC) falls after today — used to reject
 * a "future" attestation at capture time, since a notice can only record
 * something that has already happened. `now` is injectable for tests.
 */
export function isFutureNoticeDate(dateIso: string, now: DateTime = DateTime.now()): boolean {
  const date = DateTime.fromISO(dateIso, { zone: "utc" }).startOf("day");
  const today = now.toUTC().startOf("day");
  return date > today;
}

export type ContactObligationState =
  | { kind: "recorded"; notice: EpisodicNotice; timeliness: ContactTimeliness }
  | { kind: "satisfiedByForm" }
  | { kind: "due"; deadlineIso: string }
  | { kind: "overdue"; deadlineIso: string };

/**
 * SME crossover rule: an 8700-12 completed within the same 72-hour window as
 * the unplanned-contact obligation satisfies that obligation too — the two
 * requirements are otherwise fully independent. Checks a completed
 * FILING_COMPLETE submission's `submittedAt` first (taking its ISO date part,
 * since `contactTimeliness` compares date-only scalars), then a manually
 * recorded FORM_FILED_EXTERNALLY notice. A form filed before the event
 * started does not satisfy the contact — `contactTimeliness` returns
 * `beforeEvent`, not `timely`, for that case.
 */
function formSatisfiesContactObligation(event: {
  startDate: string;
  submissions: NotificationCellSubmission[];
  notices: EpisodicNotice[];
}): boolean {
  const filed = filedSubmission(event);
  const filedDate = filed?.submittedAt ? getDateLikeIsoDate(filed.submittedAt) : undefined;
  if (filedDate && contactTimeliness(event.startDate, filedDate) === "timely") {
    return true;
  }
  const filedExternallyNotice = event.notices.find(
    (notice) => notice.kind === WasteEpisodicEventNoticeKind.FormFiledExternally
  );
  if (
    filedExternallyNotice &&
    contactTimeliness(event.startDate, filedExternallyNotice.noticeDate) === "timely"
  ) {
    return true;
  }
  return false;
}

/**
 * State of an unplanned event's 72-hour EPA-contact obligation: `null` for
 * planned events (the rule doesn't apply); `recorded` once an EPA_CONTACT
 * notice exists (carrying its `timeliness` verdict against the 72-hour
 * rule); `satisfiedByForm` when no explicit contact notice exists but a
 * completed 8700-12 filing landed inside the same window (see
 * `formSatisfiesContactObligation`); otherwise `due`/`overdue`. The due/overdue
 * split uses the same calendar-day comparison as `requiresEpaContactQuestion`
 * — the question stays optional through the whole of day 3
 * (`contactDueDate`) and only flips to `overdue` once today's UTC calendar
 * date is strictly after it, i.e. day 4 — consistent with `contactTimeliness`
 * treating day 3 as timely. `now` is injectable for tests.
 */
export function resolveContactObligation(
  event: {
    type: WasteEpisodicEventType;
    startDate: string;
    notices: EpisodicNotice[];
    submissions: NotificationCellSubmission[];
  },
  now: DateTime = DateTime.now()
): ContactObligationState | null {
  if (event.type !== WasteEpisodicEventType.Unplanned) {
    return null;
  }
  const epaContactNotice = event.notices.find(
    (notice) => notice.kind === WasteEpisodicEventNoticeKind.EpaContact
  );
  if (epaContactNotice) {
    return {
      kind: "recorded",
      notice: epaContactNotice,
      timeliness: contactTimeliness(event.startDate, epaContactNotice.noticeDate),
    };
  }
  if (formSatisfiesContactObligation(event)) {
    return { kind: "satisfiedByForm" };
  }
  const deadlineIso = contactDueDate(event.startDate);
  const today = now.toUTC().startOf("day");
  const deadline = DateTime.fromISO(deadlineIso, { zone: "utc" });
  return today > deadline ? { kind: "overdue", deadlineIso } : { kind: "due", deadlineIso };
}

/**
 * Plain calendar date (no time-of-day) for the unplanned 72-hour contact
 * obligation's deadline: `startDate` plus 3 calendar days, as an ISO date
 * string. The single definition of that deadline — used both for display
 * and for the due/overdue split in `resolveContactObligation` and
 * `requiresEpaContactQuestion`.
 */
export function contactDueDate(startDate: string): string {
  const due = epaContactDeadline(parseIsoDateAsUtcDay(startDate));
  return Number.isNaN(due.getTime()) ? startDate : dateToDateString(due);
}

export type FormObligationState =
  | { kind: "filed"; timeliness: FilingTimeliness | null }
  | { kind: "submissionInProgress" }
  | { kind: "filedExternally"; notice: EpisodicNotice; timeliness: FilingTimeliness | null }
  | { kind: "notFiled" };

/**
 * State of an event's Form 8700-12 obligation: a completed filing wins over
 * everything, then a still-open submission, then a manually recorded
 * FORM_FILED_EXTERNALLY notice; otherwise nothing has been filed. `filed`
 * and `filedExternally` both carry a `timeliness` verdict against the
 * planned-only 30-day rule — `null` for an unplanned event (no deadline
 * applies) or when the filing date isn't known yet (a submission with no
 * `submittedAt`).
 */
export function resolveFormObligation(event: {
  type: WasteEpisodicEventType;
  startDate: string;
  submissions: NotificationCellSubmission[];
  notices: EpisodicNotice[];
}): FormObligationState {
  const filed = filedSubmission(event);
  if (filed) {
    return {
      kind: "filed",
      timeliness: filingTimelinessForPlannedEvent(event.type, event.startDate, filed.submittedAt),
    };
  }
  if (openSubmission(event)) {
    return { kind: "submissionInProgress" };
  }
  const filedExternallyNotice = event.notices.find(
    (notice) => notice.kind === WasteEpisodicEventNoticeKind.FormFiledExternally
  );
  if (filedExternallyNotice) {
    return {
      kind: "filedExternally",
      notice: filedExternallyNotice,
      timeliness: filingTimelinessForPlannedEvent(
        event.type,
        event.startDate,
        filedExternallyNotice.noticeDate
      ),
    };
  }
  return { kind: "notFiled" };
}

/**
 * Relative-day helper text for a picked date, e.g. "5 days ago" or "23 days
 * from now" — both date-only scalars compared in UTC calendar days (see
 * `formatEventWindow`). Returns `null` for today: a bare "today" label reads
 * as noise right under a date the user just picked, so the field's helper
 * text is simply absent in that case rather than stating the obvious. `now`
 * is injectable for tests.
 */
export function relativeDayLabel(isoDate: string, now: DateTime = DateTime.now()): string | null {
  const date = DateTime.fromISO(isoDate, { zone: "utc" }).startOf("day");
  const today = now.toUTC().startOf("day");
  const diffDays = Math.round(date.diff(today, "days").days);
  if (diffDays === 0) {
    return null;
  }
  if (diffDays > 0) {
    return diffDays === 1 ? "1 day from now" : `${diffDays} days from now`;
  }
  const pastDays = Math.abs(diffDays);
  return pastDays === 1 ? "1 day ago" : `${pastDays} days ago`;
}

/** Length of an episodic event's accumulation window, in days. */
export const EVENT_WINDOW_DAYS = 60;

/**
 * Whole days elapsed since `startDate` opened the event's accumulation
 * window, clamped to `[0, EVENT_WINDOW_DAYS]`. `now` is injectable so callers
 * (and tests) don't depend on the wall clock.
 */
export function daysUsedInWindow(startDate: string, now: DateTime = DateTime.now()): number {
  return Math.min(
    EVENT_WINDOW_DAYS,
    Math.max(0, Math.floor(now.diff(DateTime.fromISO(startDate), "days").days))
  );
}

/** True once `accumulationDeadline` (date-only, parsed in UTC) falls before today. */
export function isEventWindowLapsed(
  accumulationDeadline: string,
  now: DateTime = DateTime.now()
): boolean {
  return (
    DateTime.fromISO(accumulationDeadline, { zone: "utc" }).startOf("day") <
    now.toUTC().startOf("day")
  );
}

/**
 * Route for a waste submission linked from an episodic event card. The
 * guided stepper route additionally gates on the `WasteSubmissionStepper`
 * feature flag (see `WasteSubmissionStepper`'s redirect guard) — a tenant
 * with container tracking but not that flag bounces to the read-only detail
 * view, so callers pass that flag's value rather than always linking to the
 * stepper.
 */
export function episodicSubmissionRoute(
  tenantId: string,
  submissionId: string,
  stepperEnabled: boolean
): string {
  const base = `/o/${tenantId}/waste/submissions/${submissionId}`;
  return stepperEnabled ? `${base}/stepper` : base;
}

type UnshippedWasteContainer = {
  amount?: number | null;
  amountUnit?: UnitType | null;
  status: WasteContainerStatus;
  archivedAt?: string | null;
};

export type UnshippedEventWaste = {
  containerCount: number;
  /**
   * Total of the counted amounts with the unit spelled out ("1 gallon",
   * "68 gallons") because it sits inside a sentence. Null when an amount or
   * unit is missing or the units differ.
   */
  quantityLabel: string | null;
};

/**
 * The event waste still on site. A null amount counts as waste on hand; an
 * archived container is excluded regardless of amount. Must agree with the
 * backend's `episodicWasteGone` on what "still on site" means.
 */
export function unshippedEventWaste(containers: UnshippedWasteContainer[]): UnshippedEventWaste {
  const holding = containers.filter(
    (container) =>
      container.status !== WasteContainerStatus.Shipped &&
      container.archivedAt == null &&
      (container.amount == null || container.amount > 0)
  );

  const units = new Set(holding.map((container) => container.amountUnit));
  const allAmountsKnown = holding.every((container) => container.amount != null);
  let quantityLabel: string | null = null;
  const [unit] = units;
  if (holding.length > 0 && allAmountsKnown && units.size === 1 && unit != null) {
    const total = holding.reduce((sum, container) => sum + (container.amount ?? 0), 0);
    // The shared formatter title-cases the unit ("1 Gallon"). The label sits mid-sentence.
    quantityLabel = prettyPrintQuantity({
      amount: total,
      unit,
      abbreviateUnit: false,
    }).toLowerCase();
  }

  return { containerCount: holding.length, quantityLabel };
}

/** "1 container" / "3 containers" */
export function containerCountLabel(count: number): string {
  return count === 1 ? "1 container" : `${count} containers`;
}

/** Alert copy for an ACTIVE event whose 60-day window has lapsed. */
export function lapsedEventAlertCopy(deadlineIso: string, waste: UnshippedEventWaste): string {
  const deadline = formatContainerDate(deadlineIso);
  if (waste.containerCount === 0) {
    return `The 60-day window ended on ${deadline} and no waste from this event remains on site — it will be finalized automatically.`;
  }
  const onSitePhrase = waste.quantityLabel
    ? `${containerCountLabel(waste.containerCount)} · ${waste.quantityLabel}`
    : containerCountLabel(waste.containerCount);
  return (
    `The 60-day window ended on ${deadline} with ${onSitePhrase} still on site. ` +
    "Ship the remaining waste — it stays exempt from the on-site cap and generator status while attached, and is overdue."
  );
}

/** Why the Finalize button is disabled, or null when the event can be finalized. */
export function finalizeBlockedReason(waste: UnshippedEventWaste): string | null {
  if (waste.containerCount === 0) {
    return null;
  }
  if (waste.containerCount === 1) {
    return "1 container still holds waste from this event. Ship, empty, or detach it before finalizing.";
  }
  return `${waste.containerCount} containers still hold waste from this event. Ship, empty, or detach them before finalizing.`;
}

/** Copy for the manual "Finalize event" confirm dialog; only reachable once the waste is gone. */
export const FINALIZE_CONFIRM_COPY =
  "Every container on this event has shipped, been emptied, or been detached. Finalizing closes the window; reopen the event if more of its waste turns up.";

/**
 * Copy for the "Delete event" confirm dialog. `containerCount` is every
 * attached container, shipped or not, since a delete detaches all of them.
 */
export function deleteConfirmCopy(containerCount: number, hasSubmissionLink: boolean): string {
  let copy = "This permanently deletes the event and its recorded notices.";
  if (containerCount > 0) {
    const subject =
      containerCount === 1 ? "1 attached container" : `${containerCount} attached containers`;
    const verb = containerCount === 1 ? "stays" : "stay";
    const losesVerb = containerCount === 1 ? "loses" : "lose";
    const possessive = containerCount === 1 ? "its" : "their";
    copy +=
      ` ${subject} ${verb} in place but ${losesVerb} the 60-day episodic window — ` +
      `${possessive} standard deadlines apply.`;
  }
  if (hasSubmissionLink) {
    copy += " The linked notification submission is not retracted.";
  }
  return copy;
}

/** The relief advisory as every episodic fragment selects it. */
export type EpisodicReliefFields = {
  isAvailable: boolean;
  reasonKind?: EpisodicReliefUnavailableReason | null;
  reason?: string | null;
};

const RELIEF_CHIP_LABELS: Record<EpisodicReliefUnavailableReason, string> = {
  [EpisodicReliefUnavailableReason.Lqg]: "Relief likely not required",
  [EpisodicReliefUnavailableReason.StateNotAdopted]: "Not adopted in this state",
};

const RELIEF_NOTES: Record<EpisodicReliefUnavailableReason, string> = {
  [EpisodicReliefUnavailableReason.Lqg]:
    "Subpart L relief likely isn't required at this facility (LQG when the event started); the event is tracked as declared.",
  [EpisodicReliefUnavailableReason.StateNotAdopted]:
    "This state hasn't adopted subpart L; the event is a record only.",
};

/**
 * Copy for an event whose subpart L relief likely doesn't apply, or null when
 * it does. `body` is the server's complete explanation for the card; `note`
 * is the one-line form for tiles and attach flows.
 */
export function episodicReliefAdvisory(
  relief: EpisodicReliefFields
): { chipLabel: string; body: string; note: string } | null {
  if (relief.isAvailable || !relief.reasonKind) {
    return null;
  }
  return {
    chipLabel: RELIEF_CHIP_LABELS[relief.reasonKind],
    body: relief.reason ?? RELIEF_NOTES[relief.reasonKind],
    note: RELIEF_NOTES[relief.reasonKind],
  };
}
